/* eslint-disable react/jsx-no-duplicate-props */
import {
  Autocomplete,
  Paper,
  Popper,
  TextField,
  CircularProgress,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useEffect, useState } from "react";
import Debounce from "../../../utils/Debounce";
import "./NitroAutocomplete.css";
import { useIsComponentMounted } from "../../../hooks/useIsComponentMounted";
import { useIsMobile } from "../../../hooks/useIsMobile";

const debounce = new Debounce(1000);
const defaultFilterOptions = createFilterOptions();

const CustomPaper = (props: any) => {
  return (
    <Paper
      {...props}
      style={{
        marginTop: 10,
        borderRadius: 30,
      }}
    />
  );
};

const CustomPopper = (props: any) => {
  const { anchorEl, ...otherProps } = props;

  return (
    <Popper
      {...otherProps}
      anchorEl={anchorEl}
      style={{
        width: anchorEl?.clientWidth || 270,
        padding: "10px 0px",
        zIndex: 10200,
      }}
      placement="bottom-start"
    />
  );
};

const DefaultPopper = (props: any) => {
  const { anchorEl, ...otherProps } = props;

  return (
    <Popper
      {...otherProps}
      anchorEl={anchorEl}
      style={{
        width: anchorEl?.clientWidth || "auto",
        zIndex: 10200,
      }}
      placement="bottom-start"
    />
  );
};

const NitroAutocomplete = ({
  isFavoriteOrigins,
  value,
  setValue,
  placeholder,
  data,
  disabled,
  variant = "outlined",
  size = "small",
  config,
  autocompleteOptions,
  isLoadingOptions: externalIsLoading,
  isMobile,
}: {
  isFavoriteOrigins: boolean;
  value: string;
  setValue: (value: string) => void;
  placeholder: string;
  data:
    | { options: { label: string; value: string }[]; getRecords?: undefined }
    | {
        getRecords: (v: string) => Promise<{ label: string; value: string }[]>;
        options?: undefined;
      };
  disabled?: boolean;
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  config?: {
    shadow?: boolean;
    opaque?: boolean;
    customPaper?: boolean;
    autoFocus?: boolean;
    disableFilter?: boolean;
    inputRef?: React.MutableRefObject<any> | null;
    valueRequired?: boolean;
    className?: string;
    disableClearable?: boolean;
    hideDropdownIcon?: boolean;
  };

  autocompleteOptions?: {
    renderOption?: (params: any, option: any) => JSX.Element;
    filterOptions?: (options: any[]) => any[];
  };
  isLoadingOptions?: boolean;
  isMobile?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [records, setRecords] = useState<{ label: string; value: string }[]>(
    data.options || [],
  );
  const isTablet = useIsMobile(1334);
  const isMobileContext = isMobile ?? isTablet;

  useEffect(() => {
    if (!data.options) return;
    setRecords(data.options || []);
  }, [data]);

  // Dynamically get data
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (!data.getRecords) return;

    setRecords([]);
    if (!searchValue) {
      return;
    }

    setIsLoading(true);
    debounce.clear();
    debounce.call(async () => {
      try {
        const result = await data.getRecords(searchValue);
        setRecords(result);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.log(err);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const [text, setText] = useState("");
  const isLoadingAny = isLoading || externalIsLoading;

  useEffect(() => {
    if (!data.getRecords) return setText("");

    if (isLoadingAny) {
      setText("Loading...");
      return;
    }

    if (searchValue) {
      setText("No records...");
      return;
    }
    setText("Start typing to search...");
  }, [searchValue, isLoadingAny, data.getRecords]);

  const [inFocus, setInFocus] = useState(false);
  const isComponentMounted = useIsComponentMounted();
  useEffect(() => {
    if (!isComponentMounted) return;
    if (config?.valueRequired && !value) {
      config.inputRef?.current?.focus();
    }
  }, [config, data, value, inFocus, isComponentMounted]);

  useEffect(() => {
    if (config?.autoFocus) {
      config.inputRef?.current?.focus();
    }
  }, [config]);

  // guarantee the current value always has a matching option so MUI doesn't warn, even when value is ""
  const hasCurrentValueOption = records.some((r) => r.value === (value || ""));
  const autocompleteOptionsList = hasCurrentValueOption
    ? records
    : [
        { label: value || "", value: value || "", __placeholder: true },
        ...records,
      ];

  // only group when options actually carry a group, otherwise MUI still renders a blank group header bar
  const hasGroups = records.some((r: any) => r.group);

  return (
    <>
      {config?.opaque && inFocus && (
        <div
          className="opaque"
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(15, 44, 82,.5)",
          }}
        />
      )}
      <Autocomplete
        id="koch-location-filter"
        fullWidth
        className={config?.className}
        PaperComponent={config?.customPaper ? CustomPaper : undefined}
        PopperComponent={
          config?.customPaper
            ? CustomPopper
            : (isMobileContext && DefaultPopper) || undefined
        }
        openOnFocus
        autoFocus={config?.autoFocus}
        disabled={disabled}
        disableClearable={isFavoriteOrigins || !!config?.disableClearable}
        forcePopupIcon={config?.hideDropdownIcon ? false : undefined}
        options={autocompleteOptionsList}
        value={value}
        getOptionLabel={(option: any) => {
          if (option?.label !== undefined && option?.label !== null) {
            return option?.label;
          }
          if (option) {
            return option;
          }
          return "";
        }}
        // eslint-disable-next-line @typescript-eslint/no-shadow
        onInputChange={
          config?.disableFilter === true
            ? (e: object, v: string, reason: string) => {
                if (reason === "input") {
                  setSearchValue(v || "");
                }
              }
            : (e: object, v: string, reason: string) => {
                if (reason === "clear") {
                  if (data.getRecords) {
                    setRecords([]);
                  }
                  setSearchValue("");
                  setValue("");
                }
                if (reason === "input") {
                  setSearchValue(v || "");
                }
              }
        }
        noOptionsText={
          <div
            style={{
              padding: "10px 0px",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {isLoadingAny && <CircularProgress size={16} />}
            {text}
          </div>
        }
        onChange={(e: any, newValue) => {
          if (e.type === "keydown" && e.key === "Enter" && newValue?.value) {
            if (config?.inputRef) {
              config.inputRef.current?.blur();
            }
            setValue(newValue?.value || "");
          }

          if (!e.target.innerText) {
            return;
          }

          setValue(newValue?.value || "");

          if (config?.inputRef) {
            config.inputRef.current?.blur();
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={config?.inputRef}
            autoComplete="one-time-code"
            variant={variant}
            size={size}
            fullWidth
            value={value}
            placeholder={placeholder}
            onFocus={() => {
              setInFocus(true);
              if (value) {
                setSearchValue(value);
              }
            }}
            onBlur={() => setInFocus(false)}
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              style: {
                boxShadow: config?.shadow
                  ? "rgba(0, 0, 0, 0.3) 0px 1px 3px 0px, rgba(0, 0, 0, 0.15) 0px 4px 8px 3px"
                  : undefined,
                zIndex: (() => {
                  if (isMobileContext) {
                    if (config?.customPaper) {
                      return inFocus ? 10200 : 10150;
                    }
                    return 10200;
                  }
                  if (config?.customPaper) {
                    return inFocus ? 10200 : 10150;
                  }
                  return inFocus ? 1005 : 20;
                })(),
              },
            }}
            inputProps={{
              ...params.inputProps,

              style: {
                ...params.inputProps.style,
                padding: 0,
                resize: "horizontal",
                fontSize: 12,
              },
            }}
          />
        )}
        isOptionEqualToValue={(option, val: any) => {
          // val is the raw string passed as the Autocomplete's value, not an option object
          let v = typeof val === "string" ? val : val?.value;
          if (!v || v === "null") {
            v = "";
          }

          return option?.value === v;
        }}
        groupBy={hasGroups ? (option: any) => option?.group || "" : undefined}
        renderOption={
          autocompleteOptions?.renderOption ||
          ((params, option) => {
            return (
              <li
                {...params}
                key={option?.value}
                style={{
                  ...params.style,
                  minHeight: 30,
                  zIndex: 30,
                  padding: "10px 15px",
                }}
              >
                {option?.label || ""}
              </li>
            );
          })
        }
        filterOptions={(opts: any[], state: any) => {
          const filtered = autocompleteOptions?.filterOptions
            ? autocompleteOptions.filterOptions(opts)
            : defaultFilterOptions(opts, state);
          // hide the synthetic placeholder used to satisfy MUI's value-match check, without dropping it from `options`
          // eslint-disable-next-line no-underscore-dangle
          return filtered.filter((o: any) => !o?.__placeholder);
        }}
      />
    </>
  );
};

export default NitroAutocomplete;
